/**
 * TITULARIDAD (B1) — espejo TS del motor Rust
 * (`packages/core-rust/src/use_cases/entitlement/policy.rs`).
 *
 * El **servidor es la autoridad**: quien decide si un alumno puede reproducir
 * una grabación, entrar a la clase o descargar el certificado es el gate REST,
 * no esto. Este espejo cubre exactamente tres cosas:
 *
 *   • el VOCABULARIO (restricciones + presets) que pinta la pantalla de admin;
 *   • la RESOLUCIÓN de la cascada, para previsualizar el efecto de un cambio
 *     sin ir al servidor y para el test de paridad TS≡Rust;
 *   • los textos de explicabilidad que el alumno ve («Bloqueado por la política
 *     de tu cohorte IA-2026-1»).
 *
 * Mantener EN SYNC con el Rust: una restricción nueva se añade en los dos, y el
 * test de paridad falla si divergen.
 */

// ── Restricciones ───────────────────────────────────────────────────────────

/**
 * Cada restricción es un punto de gate con nombre.
 *
 * Lo que NO existe a propósito: `grades`. El expediente académico (notas y
 * asistencia) **no se retiene por deuda** — tiene aristas legales y es,
 * además, la palanca con más ruido y menos efecto.
 */
export const RESTRICTIONS = [
  "new_content",
  "all_content",
  "recordings",
  "live_join",
  "certificate",
  "submissions",
  "forum",
] as const;

export type Restriction = (typeof RESTRICTIONS)[number];

export function isRestriction(value: string): value is Restriction {
  return (RESTRICTIONS as readonly string[]).includes(value);
}

/** Descarta las keys desconocidas: lo que este binario no sabe aplicar no bloquea. */
export function parseRestrictions(raw: unknown): Restriction[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out = raw.filter((x): x is Restriction => typeof x === "string" && isRestriction(x));
  return [...new Set(out)].toSorted();
}

// ── Niveles de la cascada ───────────────────────────────────────────────────

/** De MENOS a MÁS específico. El orden del array ES la especificidad. */
export const POLICY_LEVELS = ["platform", "org", "program", "course", "cohort", "student"] as const;

export type PolicyLevel = (typeof POLICY_LEVELS)[number];

export function levelRank(level: PolicyLevel): number {
  return POLICY_LEVELS.indexOf(level);
}

export function isPolicyLevel(value: string): value is PolicyLevel {
  return (POLICY_LEVELS as readonly string[]).includes(value);
}

/** Nivel de origen legible; lo desconocido cae a `platform` (nadie decidió). */
export function toPolicyLevel(value: string | null | undefined): PolicyLevel {
  return value && isPolicyLevel(value) ? value : "platform";
}

export type GrantKind = "exception" | "override";

// ── Presets ─────────────────────────────────────────────────────────────────

export interface PolicySpec {
  preset: string;
  graceDays: number;
  minDebtUsdMinor: number;
  pendingPaymentHoldsDays: number;
  restrictions: Restriction[];
  locked: boolean;
}

export interface Preset {
  key: string;
  graceDays: number;
  minDebtUsdMinor: number;
  pendingPaymentHoldsDays: number;
  restrictions: Restriction[];
}

const preset = (
  key: string,
  restrictions: Restriction[],
  graceDays = 7,
  minDebtUsdMinor = 100,
  pendingPaymentHoldsDays = 5,
): Preset => ({ key, restrictions, graceDays, minDebtUsdMinor, pendingPaymentHoldsDays });

/** Registry de presets (== Rust `policy::PRESETS`, mismo orden). */
export const PRESETS: Preset[] = [
  preset("no_block", [], 0, 0, 0),
  // El preset RECOMENDADO: más palanca comercial por unidad de fricción. No
  // avanzas, no entras a la clase de hoy y no te certificas —las tres cosas son
  // server-side y por tanto REALMENTE revocables— pero conservas lo que pagaste.
  preset("recommended", ["certificate", "live_join", "new_content"]),
  preset("read_only", ["forum", "submissions"]),
  preset("block_new_content", ["live_join", "new_content"]),
  preset("block_certificate_only", ["certificate"]),
  // Todo. **Sin notas ni asistencia**.
  preset("block_all", [
    "all_content",
    "certificate",
    "forum",
    "live_join",
    "recordings",
    "submissions",
  ]),
];

export const RECOMMENDED_PRESET_KEY = "recommended";

export function presetByKey(key: string): Preset | undefined {
  return PRESETS.find((p) => p.key === key);
}

export function specFromPreset(p: Preset): PolicySpec {
  return {
    preset: p.key,
    graceDays: p.graceDays,
    minDebtUsdMinor: p.minDebtUsdMinor,
    pendingPaymentHoldsDays: p.pendingPaymentHoldsDays,
    restrictions: p.restrictions.toSorted(),
    locked: false,
  };
}

/**
 * Default de FÁBRICA: fallar abierto.
 *
 * La titularidad no es una frontera de confidencialidad —el contenido se vende,
 * no es secreto— y la asimetría de coste va en una sola dirección: bloquear a
 * un alumno que sí pagó cuesta dinero, soporte y reputación; no bloquear a un
 * moroso deja la deuda intacta y la cobranza sigue persiguiéndole.
 */
export function factorySpec(): PolicySpec {
  return specFromPreset(PRESETS[0]!);
}

// ── Resolución de la cascada ────────────────────────────────────────────────

export interface PolicyLayer {
  level: PolicyLevel;
  id: string;
  scopeId?: string | null;
  label?: string | null;
  spec: PolicySpec;
  grantKind?: GrantKind | null;
  expiresOn?: string | null;
}

export interface EffectivePolicy {
  spec: PolicySpec;
  sourceLevel: PolicyLevel;
  sourceId?: string | null;
  sourceLabel?: string | null;
  grantKind?: GrantKind | null;
  expiresOn?: string | null;
  lockedAtLevel?: PolicyLevel | null;
  /** `false` = nadie configuró nada; se usa el default de fábrica. */
  configured: boolean;
}

/**
 * El nivel MÁS específico gana, y una política `locked` es un TECHO que anula
 * todo lo que haya por debajo.
 *
 * Sin el techo la jerarquía no tiene freno de emergencia: la plataforma no
 * podría decir «ninguna academia puede bloquear a nadie» sin quitarle a las
 * academias el resto de la configurabilidad.
 */
export function resolveEffectivePolicy(layers: PolicyLayer[]): EffectivePolicy {
  if (layers.length === 0) {
    return {
      spec: factorySpec(),
      sourceLevel: "platform",
      sourceId: null,
      sourceLabel: null,
      grantKind: null,
      expiresOn: null,
      lockedAtLevel: null,
      configured: false,
    };
  }
  const sorted = layers.toSorted((a, b) => levelRank(a.level) - levelRank(b.level));
  // El `locked` MENOS específico manda: uno de plataforma está por encima del
  // de una organización.
  const lockedLayer = sorted.find((l) => l.spec.locked);
  const winner = lockedLayer ?? sorted.at(-1)!;
  return {
    spec: winner.spec,
    sourceLevel: winner.level,
    sourceId: winner.id,
    sourceLabel: winner.label ?? null,
    grantKind: winner.grantKind ?? null,
    expiresOn: winner.expiresOn ?? null,
    lockedAtLevel: lockedLayer ? lockedLayer.level : null,
    configured: true,
  };
}

// ── Estado materializado (lo que el cliente LEE por sync) ───────────────────

export type EntitlementStateValue = "entitled" | "grace" | "restricted";

export interface EntitlementStateRow {
  scopeLevel: string;
  scopeId?: string | null;
  state: EntitlementStateValue;
  restrictions: Restriction[];
  sourceLevel: PolicyLevel;
  sourceLabel?: string | null;
  grantKind?: GrantKind | null;
  reasonCode?: string | null;
  unblocksAt?: string | null;
}

/**
 * ¿Este estado retira `restriction`? `all_content` implica `new_content`
 * (bloquear todo incluye lo nuevo) — la implicación vive en un solo sitio para
 * que ningún consumidor la olvide.
 */
export function blocks(row: Pick<EntitlementStateRow, "restrictions">, r: Restriction): boolean {
  if (row.restrictions.includes(r)) {
    return true;
  }
  return r === "new_content" && row.restrictions.includes("all_content");
}

/** El peor estado de una lista (para el aviso global de la academia). */
export function worstState(rows: Pick<EntitlementStateRow, "state">[]): EntitlementStateValue {
  if (rows.some((r) => r.state === "restricted")) {
    return "restricted";
  }
  if (rows.some((r) => r.state === "grace")) {
    return "grace";
  }
  return "entitled";
}
